import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, ArrowRight, ArrowLeft, Info } from "lucide-react";
import { Field, Input, Textarea, ChoiceGrid, FileUpload, Consent } from "../components/FormBits";
import { useSeo, Reveal } from "../lib/motion";
import { api, formatError } from "../lib/api";
import { isEmail, isFrenchPhone, isCityPostal } from "../lib/validate";
import { toast } from "sonner";

const UNIVERS = ["Climatisation", "Froid commercial"];
const NATURES = ["Installation", "Maintenance", "Dépannage", "Remplacement"];
const BATIMENTS = ["Maison", "Appartement", "Commerce", "Bureau", "Local professionnel", "Autre"];
const ONS = ["Oui", "Non", "Je ne sais pas"];
const ON = ["Oui", "Non"];
const UNITS = ["1", "2", "3", "4", "5+", "Je ne sais pas"];
const DISPOSITIFS = ["TVA 5,5 %", "TVA 10 %", "Aide CEE", "TVA 5,5 % + Aide CEE", "TVA 10 % + Aide CEE", "Aucun / non renseigné"];
const AIDE_PARAM = { cee: "Aide CEE", tva10: "TVA 10 %", tva55: "TVA 5,5 %" };

const OPTS = {
  climInstall: ["Mono-split", "Bi-split", "Multi-split", "Gainable", "Cassette / climatisation professionnelle", "Autre"],
  climDep: ["Mono-split", "Multi-split", "Gainable", "Cassette / climatisation professionnelle", "Autre"],
  froidEquip: ["Chambre froide positive", "Chambre froide négative", "Meuble réfrigéré", "Vitrine réfrigérée", "Groupe frigorifique", "Autre"],
  activite: ["Restaurant", "Boulangerie", "Boucherie", "Commerce alimentaire", "Supermarché", "Autre"],
  motif: ["Panne", "Vétusté", "Performance", "Projet différent", "Autre"],
};

// Configuration par (univers :: nature)
const CONFIG = {
  "Climatisation::Installation": { subLabel: "Type de climatisation", subOptions: OPTS.climInstall, fields: [
    { key: "batiment", label: "Type de bâtiment", type: "choice", options: BATIMENTS },
    { key: "surface", label: "Surface approximative (m²)", type: "number" },
    { key: "pieces", label: "Nombre de pièces", type: "number" },
    { key: "existante", label: "Installation existante", type: "choice", options: ON },
  ] },
  "Climatisation::Maintenance": { subLabel: "Quel type d’installation souhaitez-vous entretenir ?", subOptions: OPTS.climInstall, fields: [] },
  "Climatisation::Dépannage": { subLabel: "Type d’installation", subOptions: OPTS.climDep, fields: [
    { key: "marque", label: "Marque", type: "text" },
    { key: "modele", label: "Modèle (si connu)", type: "text" },
    { key: "code_defaut", label: "Code défaut éventuel", type: "text" },
    { key: "arret", label: "Installation totalement arrêtée", type: "choice", options: ONS },
    { key: "panne", label: "Description de la panne", type: "textarea" },
  ] },
  "Climatisation::Remplacement": { subLabel: "Type d’installation existante", subOptions: OPTS.climInstall, fields: [
    { key: "marque", label: "Marque", type: "text" },
    { key: "age", label: "Âge approximatif", type: "text" },
    { key: "nombre_unites", label: "Nombre d’unités", type: "number" },
    { key: "motif", label: "Motif du remplacement", type: "choice", options: OPTS.motif },
  ] },
  "Froid commercial::Installation": { subLabel: "Type d’équipement", subOptions: OPTS.froidEquip, fields: [
    { key: "activite", label: "Type d’activité", type: "choice", options: OPTS.activite },
    { key: "volume", label: "Dimensions / volume (si pertinent)", type: "text" },
    { key: "temperature", label: "Température souhaitée (si connue)", type: "text" },
    { key: "description", label: "Description", type: "textarea" },
  ] },
  "Froid commercial::Maintenance": { subLabel: "Type d’équipement", subOptions: OPTS.froidEquip, surDevis: true, fields: [
    { key: "nombre_equip", label: "Nombre d’équipements concernés", type: "number" },
    { key: "marque", label: "Marque (si connue)", type: "text" },
    { key: "description", label: "Description", type: "textarea" },
  ] },
  "Froid commercial::Dépannage": { subLabel: "Type d’équipement en panne", subOptions: OPTS.froidEquip, fields: [
    { key: "marque", label: "Marque", type: "text" },
    { key: "modele", label: "Modèle (si connu)", type: "text" },
    { key: "code_defaut", label: "Code défaut éventuel", type: "text" },
    { key: "arret", label: "Installation totalement arrêtée", type: "choice", options: ONS },
    { key: "temperature", label: "Température actuelle (si connue)", type: "text" },
    { key: "panne", label: "Description de la panne", type: "textarea" },
  ] },
  "Froid commercial::Remplacement": { subLabel: "Type d’équipement existant", subOptions: OPTS.froidEquip, fields: [
    { key: "marque", label: "Marque", type: "text" },
    { key: "age", label: "Âge approximatif", type: "text" },
    { key: "nombre_equip", label: "Nombre d’équipements", type: "number" },
    { key: "motif", label: "Motif du remplacement", type: "choice", options: OPTS.motif },
  ] },
};

const LABELS = {
  subtype: "Type d’installation ou équipement", dispositif_aide_tva: "Dispositif / aide sélectionné",
  batiment: "Type de bâtiment", surface: "Surface (m²)",
  pieces: "Nombre de pièces", existante: "Installation existante", marque: "Marque", modele: "Modèle",
  code_defaut: "Code défaut", arret: "Installation totalement arrêtée", panne: "Description de la panne",
  age: "Âge approximatif", nombre_unites: "Nombre d’unités", nombre_equip: "Nombre d’équipements",
  motif: "Motif du remplacement", activite: "Type d’activité", volume: "Dimensions / volume",
  temperature: "Température", units: "Nombre d’unités",
};

// Tarif indicatif : uniquement Climatisation > Maintenance
function tarifText(f) {
  if (f.univers === "Climatisation" && f.nature === "Maintenance") {
    const t = f.subtype, u = f.units;
    if (t === "Mono-split") return "130 € HT";
    if (t === "Bi-split") return "150 € HT";
    if (t === "Multi-split") {
      if (u === "3") return "200 € HT";
      if (u === "4") return "240 € HT";
      if (u === "5") return "280 € HT";
      if (u === "5+") return "À partir de 280 € HT";
      return "À partir de 200 € HT selon le nombre d’unités et la configuration.";
    }
    return "Sur devis"; // Gainable, Cassette, Autre
  }
  return "";
}

function TarifBlock({ tarif, indicatif = true }) {
  if (!tarif) return null;
  return (
    <div className="mt-6 rounded-md border border-ice/30 bg-ice/5 p-5" data-testid="tarif-block">
      <p className="text-xs font-bold uppercase tracking-widest text-ice">{indicatif ? "Tarif indicatif HT" : "Tarif"}</p>
      <p className="font-heading font-black text-2xl md:text-3xl text-ink mt-2">{tarif}</p>
      {indicatif && <p className="text-sm text-muted2 mt-3 flex items-start gap-2"><Info size={15} className="text-ice mt-0.5 shrink-0" /> Le tarif peut varier selon la configuration, l’accessibilité et l’état de l’installation.</p>}
    </div>
  );
}

export default function Devis() {
  useSeo("Demande de devis", "Demandez un devis en ligne pour vos besoins de climatisation ou de froid commercial en Alsace.");
  const [params] = useSearchParams();
  const initF = () => {
    const init = {};
    const aide = AIDE_PARAM[params.get("aide")];
    if (aide) init.dispositif_aide_tva = aide;
    const u = { climatisation: "Climatisation", "froid-commercial": "Froid commercial" }[params.get("univers")];
    if (u) init.univers = u;
    const n = { installation: "Installation", maintenance: "Maintenance", depannage: "Dépannage", remplacement: "Remplacement" }[params.get("nature")];
    if (u && n) init.nature = n;
    return init;
  };
  const [step, setStep] = useState(() => { const f0 = initF(); return f0.nature ? 2 : f0.univers ? 1 : 0; });
  const [f, setF] = useState(initF);
  const [files, setFiles] = useState([]);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const on = (k) => (e) => set(k, e.target.value);

  const key = `${f.univers}::${f.nature}`;
  const branch = CONFIG[key] || null;
  const climMaint = f.univers === "Climatisation" && f.nature === "Maintenance";
  const froidMaint = f.univers === "Froid commercial" && f.nature === "Maintenance";
  const askUnits = climMaint && (f.subtype === "Multi-split" || f.subtype === "Cassette / climatisation professionnelle");
  const showAide = f.univers === "Climatisation" && (f.nature === "Installation" || f.nature === "Remplacement");
  const tarif = tarifText(f);

  const selectUnivers = (v) => setF((p) => ({ univers: v, ...(v === "Climatisation" ? { dispositif_aide_tva: p.dispositif_aide_tva } : {}) }));
  const selectNature = (v) => setF((p) => {
    const keepAide = p.univers === "Climatisation" && (v === "Installation" || v === "Remplacement");
    return { univers: p.univers, nature: v, ...(keepAide ? { dispositif_aide_tva: p.dispositif_aide_tva } : {}) };
  });
  const selectSubtype = (v) => {
    if (climMaint) {
      const auto = v === "Mono-split" ? "1" : v === "Bi-split" ? "2" : "";
      setF((p) => ({ ...p, subtype: v, units: auto }));
    } else set("subtype", v);
  };

  const steps = !f.univers ? ["univers"]
    : !f.nature ? ["univers", "nature"]
    : ["univers", "nature", "subtype", ...(showAide ? ["dispositif"] : []), ...(askUnits ? ["units"] : []), ...((branch?.fields.length) ? ["details"] : []), "ville", "coord", "photos", "recap"];
  const total = steps.length;
  const type = steps[step];

  const titles = {
    univers: "Quel est votre besoin ?", nature: "Que souhaitez-vous faire ?",
    subtype: branch?.subLabel || "Type", dispositif: "Dispositif d’aide / TVA (optionnel)",
    units: "Combien d’unités intérieures sont concernées ?",
    details: "Informations complémentaires", ville: "Où se situe le projet ?",
    coord: "Vos coordonnées", photos: "Photos ou plans", recap: "Validation",
  };

  const validateStep = () => {
    if (type === "univers") return !!f.univers || "Sélectionnez un univers.";
    if (type === "nature") return !!f.nature || "Sélectionnez la nature de la demande.";
    if (type === "subtype") return !!f.subtype || "Merci de sélectionner une option.";
    if (type === "units") return !!f.units || "Indiquez le nombre d’unités concernées.";
    if (type === "ville") return isCityPostal(f.ville) || "Saisissez une ville et/ou un code postal valide (5 chiffres).";
    if (type === "coord") {
      if (!f.nom) return "Merci d’indiquer votre nom.";
      if (!isFrenchPhone(f.telephone)) return "Numéro de téléphone français invalide.";
      if (!isEmail(f.email)) return "Adresse e-mail invalide.";
      return true;
    }
    if (type === "recap") return consent || "Merci d’accepter la politique de confidentialité.";
    return true;
  };

  const next = () => { const v = validateStep(); if (v !== true) return toast.error(v); setStep((s) => Math.min(s + 1, total - 1)); };
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    const v = validateStep();
    if (v !== true) return toast.error(v);
    setBusy(true);
    try {
      const data = { ...f };
      if (showAide) {
        if (!data.dispositif_aide_tva) data.dispositif_aide_tva = "Aucun / non renseigné";
      } else {
        delete data.dispositif_aide_tva;
      }
      if (climMaint && tarif) data.tarif_indicatif = tarif;
      await api.post("/submissions", { type: "devis", data, files: files.map((x) => x.url) });
      setDone(true);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const renderField = (d) => {
    if (d.type === "choice") return <Field key={d.key} label={d.label} className="sm:col-span-2"><ChoiceGrid name={d.key} options={d.options} value={f[d.key] || ""} onChange={(val) => set(d.key, val)} /></Field>;
    if (d.type === "textarea") return <Field key={d.key} label={d.label} className="sm:col-span-2"><Textarea value={f[d.key] || ""} onChange={on(d.key)} /></Field>;
    return <Field key={d.key} label={d.label}><Input type={d.type === "number" ? "number" : "text"} min={d.type === "number" ? "0" : undefined} value={f[d.key] || ""} onChange={on(d.key)} /></Field>;
  };

  if (done) return (
    <div className="pt-40 pb-40 max-w-2xl mx-auto px-6 text-center">
      <div data-testid="devis-success" className="bg-cloud border border-line rounded-md p-12">
        <CheckCircle2 className="mx-auto text-ice" size={54} />
        <h1 className="font-heading font-black text-3xl text-ink mt-6">Votre demande a bien été transmise à HP Froid.</h1>
        <p className="text-maintext mt-4">Nous reviendrons vers vous après analyse de votre projet.</p>
      </div>
    </div>
  );

  const skip = ["univers", "nature", "subtype", "dispositif_aide_tva", "nom", "telephone", "email", "ville", "description", "tarif_indicatif", "panne"];
  const recapEntries = Object.entries(f).filter(([k, v]) => v && !skip.includes(k));

  return (
    <div className="pt-28 pb-24 bg-cloud min-h-screen">
      <section className="max-w-3xl mx-auto px-6 md:px-12 pt-8">
        <span className="text-xs font-bold uppercase tracking-[0.25em] text-ice">Demande de devis</span>
        <h1 className="font-heading font-black tracking-tight text-3xl md:text-5xl text-ink mt-4">Parlons de votre projet.</h1>

        <div className="mt-8 mb-6">
          <div className="flex justify-between text-xs font-mono text-muted2 mb-2">
            <span>Étape {step + 1} / {total}</span>
            <span>{Math.round(((step + 1) / total) * 100)}%</span>
          </div>
          <div className="h-1.5 bg-line rounded-full overflow-hidden">
            <div className="h-full bg-ice transition-[width] duration-500" style={{ width: `${((step + 1) / total) * 100}%` }} />
          </div>
        </div>

        <Reveal key={step} y={20}>
          <div className="bg-white border border-line rounded-md p-7 md:p-9">
            <h2 className="font-heading font-bold text-2xl text-ink mb-6">{titles[type]}</h2>

            {type === "univers" && <ChoiceGrid name="univers" options={UNIVERS} value={f.univers || ""} onChange={selectUnivers} />}
            {type === "nature" && <ChoiceGrid name="nature" options={NATURES} value={f.nature || ""} onChange={selectNature} />}

            {type === "subtype" && (
              <>
                <ChoiceGrid name="subtype" options={branch.subOptions} value={f.subtype || ""} onChange={selectSubtype} />
                {climMaint && f.subtype && <TarifBlock tarif={tarif} />}
              </>
            )}

            {type === "dispositif" && (
              <>
                <p className="text-maintext mb-5 leading-relaxed">Un dispositif d’aide ou de TVA réduite vous intéresse-t-il pour ce projet ? (facultatif — l’éligibilité est vérifiée avant devis)</p>
                <ChoiceGrid name="dispositif_aide_tva" options={DISPOSITIFS} value={f.dispositif_aide_tva || ""} onChange={(v) => set("dispositif_aide_tva", v)} />
              </>
            )}

            {type === "units" && (
              <>
                <ChoiceGrid name="units" options={f.subtype === "Multi-split" ? ["3", "4", "5", "5+", "Je ne sais pas"] : UNITS} value={f.units || ""} onChange={(v) => set("units", v)} />
                <TarifBlock tarif={tarif} />
              </>
            )}

            {type === "details" && (
              <>
                <div className="grid sm:grid-cols-2 gap-5">{branch.fields.map(renderField)}</div>
                {froidMaint && <TarifBlock tarif="Sur devis" indicatif={false} />}
              </>
            )}

            {type === "ville" && (
              <div className="max-w-sm">
                <Field label="Ville / code postal" required hint="Ex. Illkirch-Graffenstaden 67400">
                  <Input data-testid="devis-ville" value={f.ville || ""} onChange={on("ville")} placeholder="Ex. Illkirch-Graffenstaden 67400" />
                </Field>
              </div>
            )}

            {type === "coord" && (
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Nom" required><Input data-testid="devis-nom" value={f.nom || ""} onChange={on("nom")} /></Field>
                <Field label="Téléphone" required><Input data-testid="devis-tel" type="tel" value={f.telephone || ""} onChange={on("telephone")} placeholder="06 12 34 56 78" /></Field>
                <Field label="E-mail" required><Input data-testid="devis-email" type="email" value={f.email || ""} onChange={on("email")} /></Field>
              </div>
            )}

            {type === "photos" && <Field label="Ajoutez des photos ou des plans (optionnel)"><FileUpload files={files} setFiles={setFiles} testid="devis-files" /></Field>}

            {type === "recap" && (
              <div className="space-y-6">
                <div className="bg-cloud rounded-md p-5 text-sm text-maintext grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
                  <p><strong className="text-ink">Univers :</strong> {f.univers}</p>
                  <p><strong className="text-ink">Nature de la demande :</strong> {f.nature}</p>
                  {showAide && f.dispositif_aide_tva && <p className="sm:col-span-2"><strong className="text-ink">Dispositif / aide sélectionné :</strong> {f.dispositif_aide_tva}</p>}
                  <p className="sm:col-span-2"><strong className="text-ink">Type d’installation ou équipement :</strong> {f.subtype || "—"}</p>
                  {recapEntries.map(([k, v]) => (
                    <p key={k}><strong className="text-ink">{LABELS[k] || k} :</strong> {v}</p>
                  ))}
                  <p className="sm:col-span-2"><strong className="text-ink">Ville / code postal :</strong> {f.ville}</p>
                  {climMaint && tarif && <p className="sm:col-span-2"><strong className="text-ink">Tarif indicatif HT :</strong> {tarif}</p>}
                  {(f.description || f.panne) && <p className="sm:col-span-2"><strong className="text-ink">Description :</strong> {f.description || f.panne}</p>}
                  <p className="sm:col-span-2 border-t border-line pt-2 mt-1"><strong className="text-ink">Contact :</strong> {f.nom} · {f.telephone} · {f.email}</p>
                  {files.length > 0 && <p className="sm:col-span-2"><strong className="text-ink">Pièces jointes :</strong> {files.map((x) => x.name).join(", ")}</p>}
                </div>
                {climMaint && tarif && <p className="text-xs text-muted2">Tarif indicatif HT — le tarif peut varier selon la configuration, l’accessibilité et l’état de l’installation.</p>}
                <Consent checked={consent} onChange={setConsent} />
              </div>
            )}
          </div>
        </Reveal>

        <div className="flex justify-between mt-6">
          <button onClick={prev} disabled={step === 0} data-testid="devis-prev" className="inline-flex items-center gap-2 px-6 py-3 rounded-md font-bold text-ink border border-line bg-white disabled:opacity-40 hover:border-ice/40 transition-colors">
            <ArrowLeft size={17} /> Retour
          </button>
          {step < total - 1 ? (
            <button onClick={next} data-testid="devis-next" className="inline-flex items-center gap-2 px-8 py-3 rounded-md font-bold text-white bg-ice hover:bg-icehover transition-transform hover:scale-105">
              Continuer <ArrowRight size={17} />
            </button>
          ) : (
            <button onClick={submit} disabled={busy} data-testid="devis-submit" className="inline-flex items-center gap-2 px-8 py-3 rounded-md font-bold text-white bg-ink hover:bg-inkhover transition-transform hover:scale-105 disabled:opacity-60">
              {busy && <Loader2 className="animate-spin" size={18} />} Envoyer ma demande
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
